import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Translation function type for relative time
type TimeTranslations = {
  justNow: string;
  minutesAgo: string;
  hoursAgo: string;
  daysAgo: string;
};

export function formatRelativeTime(date: Date | string, translations?: TimeTranslations): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  // Use translations if provided, otherwise fall back to English
  if (translations) {
    if (diffSec < 60) return translations.justNow;
    if (diffMin < 60) return translations.minutesAgo.replace('{count}', String(diffMin));
    if (diffHr < 24) return translations.hoursAgo.replace('{count}', String(diffHr));
    if (diffDay < 7) return translations.daysAgo.replace('{count}', String(diffDay));
  } else {
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
  }
  return formatDate(d);
}

/**
 * Format large numbers with k, M, B suffixes.
 * e.g. 999 -> "999", 1200 -> "1.2k", 403000 -> "403k", 2100000 -> "2.1M"
 */
export function formatCount(num: number): string {
  if (num >= 1_000_000_000) {
    const v = num / 1_000_000_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '')) + 'B';
  }
  if (num >= 1_000_000) {
    const v = num / 1_000_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '')) + 'M';
  }
  if (num >= 1_000) {
    const v = num / 1_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '')) + 'k';
  }
  return num.toString();
}

/**
 * Strip <think>...</think> blocks from a string, returning only the visible text.
 * Also handles incomplete/unclosed <think> tags (streaming).
 */
export function stripThinkTags(text: string): string {
  let cleaned = text.replace(/<think>[\s\S]*?<\/think>/gi, '');
  cleaned = cleaned.replace(/<think>[\s\S]*$/gi, '');
  return cleaned.trim();
}

export function generateReferralCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Generate a username from a display name (sync, no X check).
 * @deprecated Use generateUniqueUsername from '@/lib/username/validation' for new code.
 */
export function generateUsername(name: string): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const suffix = Math.random().toString(36).substring(2, 6);
  return `${base}${suffix}`;
}

/** Human-readable byte count: 1.2 GB / 340 MB / 12 KB / 512 B. */
export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}
